import type { RewardsSummary } from '@pebblecode/shared';

import { onLinkClick } from '../app/router.js';
import { Icon } from '../components/Icon.js';
import { useT } from '../i18n/index.js';
import { useExerciseTitles } from '../state/catalog.js';
import { useUiLang } from '../state/prefs.js';
import { dismissRewardToast, rewardToastsStore, useRewards } from '../state/rewards.js';
import { useStore } from '../state/store.js';
import { eventLabel } from './rewardText.js';
import { BadgeMedal, ProfileIcon } from './RewardIcons.js';

/** Progress toward the next level. */
export function LevelBar({ s }: { s: RewardsSummary }) {
  const t = useT();
  const span = s.nextLevelAt - s.levelStart;
  const pct = Math.round(((s.points - s.levelStart) / span) * 100);
  return (
    <div className="level-bar">
      <div
        className="level-bar-track"
        role="progressbar"
        aria-valuemin={s.levelStart}
        aria-valuemax={s.nextLevelAt}
        aria-valuenow={s.points}
        aria-label={t.rewards.level(s.level)}
      >
        <div className="level-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span>{t.rewards.toNext(s.nextLevelAt - s.points, s.level + 1)}</span>
    </div>
  );
}

/** "⬤ Lv 3 · 180" in the top bar, linking to the progress page. */
export function RewardsChip() {
  const t = useT();
  const s = useRewards();
  if (!s) return null;
  return (
    <a
      href="/me"
      onClick={onLinkClick}
      className="rewards-chip"
      aria-label={t.rewards.chip(s.level, s.points)}
      title={t.rewards.chip(s.level, s.points)}
    >
      <span className="rewards-chip-icon">
        <ProfileIcon id={s.equipped.icon} size={15} />
      </span>
      <span className="rewards-chip-level">
        <span className="muted-label">{t.rewards.levelShort}</span> {s.level}
      </span>
      <span className="rewards-chip-points">· {s.points}</span>
    </a>
  );
}

/** "+20 · Solved Sum of digits", "New badge", "Level 3!" — bottom right, gone after a few seconds. */
export function RewardToasts() {
  const t = useT();
  const lang = useUiLang();
  const titles = useExerciseTitles(lang);
  const toasts = useStore(rewardToastsStore);
  if (toasts.length === 0) return null;
  return (
    <div className="reward-toasts" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <button
          key={toast.key}
          className={`reward-toast ${toast.kind}`}
          onClick={() => dismissRewardToast(toast.key)}
        >
          {toast.kind === 'points' && (
            <>
              <span className="reward-toast-points">+{toast.event.points}</span>
              <span>{eventLabel(toast.event, t, titles)}</span>
            </>
          )}
          {toast.kind === 'badge' && (
            <>
              <BadgeMedal id={toast.badge} earned size={28} />
              <span>{t.rewards.toastBadge(t.rewards.badge[toast.badge]?.[0] ?? toast.badge)}</span>
            </>
          )}
          {toast.kind === 'level' && (
            <>
              <Icon name="sparkle" size={18} />
              <span>{t.rewards.toastLevel(toast.level)}</span>
            </>
          )}
        </button>
      ))}
    </div>
  );
}
